import { getCurrentConversation } from 'conversations/modules/conversation/selectors'
import { byId } from 'conversations/modules/endUser/selectors'
import { getFilteredConversationSections } from 'conversations/modules/user/sections/selectors'
import { getCurrentInboxId } from 'conversations/modules/user/selectors'
import { createConditionsArray, isQueueTimeSection } from 'conversations/utils/conditionsUtil'
import { buildConversationsQuery } from 'conversations/utils/ConversationSearchUtil'
import { STATUSES_FOR_PILL } from 'conversations/utils/pillUtil'
import { Action } from 'redux'
import { Epic } from 'redux-observable'
import { filter, mergeMap } from 'rxjs/operators'
import {
  fetchAllConversationsAsync,
  fetchConversationAsync,
  fetchCounts,
} from 'shared/store/actions/conversation'
import { fetchEndUsersAction } from 'shared/store/actions/endUser'
import { fetchMessagesAsync } from 'shared/store/actions/message'
import { fetchSectionsAsync } from 'shared/store/actions/user/sections'
import { refreshApplicationState } from 'shared/store/actions/websocket'
import { SortOrder } from 'shared/typings/search/SortOrder'
import { localStore } from 'shared/utils/storage'

import { isActionOf, RootAction, RootState, Services } from 'typesafe-actions'

export const refreshApplicationStateOnWebsocketReconnect: Epic<
  RootAction,
  RootAction,
  RootState,
  Services
> = (action$, state) => {
  return action$.pipe(
    filter(isActionOf(refreshApplicationState)),
    mergeMap(() => {
      const actions: Action[] = []

      const activeConversationId = getCurrentConversation(state.value)?.id
      const inboxId = getCurrentInboxId(state.value)
      const sections = getFilteredConversationSections(state.value)
      const currentEndUsers = byId(state.value)

      // get all the open sections based on what is in local storage
      const sectionLSKeys = localStore
        .getAll()
        .filter((item: Record<string, string>) => Object.keys(item)[0].includes('DRIFT_sidebar'))
      const openSectionIds = sectionLSKeys
        .map((item: string) => Object.keys(item)[0].match(/(?<=DRIFT_sidebar_).*(?=_expanded)/gm))
        .map((matches: string[]) => matches[0])

      if (activeConversationId) {
        // Note: this will not re-fetch end users that are already in the store
        actions.push(fetchConversationAsync.request(activeConversationId))
        // UX - if user is scrolled up, do we scroll them to the bottom?
        actions.push(
          fetchMessagesAsync.request({
            conversationId: activeConversationId,
            loadMore: false,
            limit: 50,
          })
        )
      }

      actions.push(fetchEndUsersAction(Object.keys(currentEndUsers)))

      openSectionIds.forEach((sectionId: string) => {
        const sectionConfig = sections.find((section) => section.id === sectionId)
        if (sectionConfig) {
          const subscriptionId = sectionConfig.subscriptionId
          const isQueueTimeOnlySection = isQueueTimeSection(
            createConditionsArray(sectionConfig.condition)
          )
          const sortOrder = isQueueTimeOnlySection ? SortOrder.ASC : SortOrder.DESC

          const LS_KEY = `sidebar_${sectionConfig.id}_expanded`
          const selectedPill = localStore.get(LS_KEY)
          const viewType = `${sectionConfig.id}-${selectedPill}`

          actions.push(
            fetchAllConversationsAsync.request({
              query: buildConversationsQuery({
                statuses: STATUSES_FOR_PILL[selectedPill],
                inboxId,
                sortOrder,
                limit: 20,
              }),
              cursor: undefined,
              viewType,
              subscriptionId,
              refreshState: true,
            })
          )

          actions.push(fetchCounts({ subscriptionId, inboxId, sectionUuid: sectionConfig.id }))
        }
      })

      actions.push(fetchSectionsAsync.request(undefined))

      return actions
    })
  )
}
